import classNames from "classnames";
import React from "react";
import { t } from "ttag";

import { interpolate } from "@thelabnyc/thelabui/src/utils/i18n";

import creditCards from "../../../../img/checkout/credit-cards.png";
import { Image } from "../../../components/Image";
import { FormNumber } from "../../../forms/FormNumber";
import { FormTextbox } from "../../../forms/FormTextbox";
import { useMountEffect } from "../../../hooks/useMountEffect";
import { format } from "../../../utils/format";
import { focusElement } from "../../../utils/keyboardFocus";
import { IPaymentMethodFormProps } from "../PaymentMethod.interfaces";
import { PRIMARY_PAYMENT_METHOD_KEY } from "../constants";
import { IBluefinPayment, IPayment } from "../reducers.interfaces";
import { checkBluefinFrame } from "./frameCheck";

interface IProps extends IPaymentMethodFormProps {
    setPaymentMethodFields: (
        methodKey: string,
        fields: Partial<IPayment>,
    ) => void;
    changeAmount: (methodKey: string, amount: string) => void;
}

// Keep this in sync with tsicommon.cybersource.methods.BluefinPaymentMethodSerializer.
const BLUEFIN_PAYMENT_DATA_MAX_LENGTH = 256;

// Long enough that the check never fires mid-swipe (readers emit a keystroke
// every few milliseconds), short enough to be seen before submitting.
const FRAME_WARNING_DEBOUNCE_MS = 750;

const getMethodData = (
    form: IProps["form"],
    methodKey: string,
): IBluefinPayment => {
    const data = form.payment_methods[methodKey];
    let cybData: IBluefinPayment | null = null;
    if (data.method_type === "bluefin") {
        cybData = data;
    }
    const baseFields: IBluefinPayment = {
        method_type: "bluefin",
        amount: "",
        pay_balance: false,
        payment_data: "",
    };
    return {
        ...baseFields,
        ...cybData,
    };
};

export const Bluefin = (props: IProps): React.ReactNode => {
    const textBoxRef = React.useRef<HTMLTextAreaElement>(null);
    const [frameWarning, setFrameWarning] = React.useState<string | null>(null);

    const methodData = getMethodData(props.form, props.methodKey);
    const paymentData = methodData.payment_data;

    // The field is uncontrolled so the reader's keystrokes are never erased
    // by a value round-trip (#35361). When redux resets the payload (declined
    // payment, method switch), clear the DOM to match.
    React.useEffect(() => {
        const elem = textBoxRef.current;
        if (paymentData === "" && elem && elem.value !== "") {
            elem.value = "";
        }
    }, [paymentData]);

    React.useEffect(() => {
        const timer = setTimeout(() => {
            const check = checkBluefinFrame(paymentData);
            if (check.status !== "incomplete") {
                setFrameWarning(null);
                return;
            }
            const received = String(check.received);
            const expected = String(check.expected);
            setFrameWarning(
                t`Only ${received} of the ${expected} characters reported by the card reader arrived. Please clear the field and re-enter the card.`,
            );
        }, FRAME_WARNING_DEBOUNCE_MS);
        return () => {
            clearTimeout(timer);
        };
    }, [paymentData]);

    useMountEffect(() => {
        // Set focus to the input field, so Bluefin entry will not be ignored
        // Only try to focus the TextBox if it is visible
        const inputElem = textBoxRef.current;
        if (
            inputElem &&
            inputElem.offsetHeight > 0 &&
            props.methodKey === PRIMARY_PAYMENT_METHOD_KEY
        ) {
            focusElement(inputElem);
        }
    });

    const onChange = (event: React.FormEvent<HTMLTextAreaElement>) => {
        props.setPaymentMethodFields(props.methodKey, {
            [event.currentTarget.name]: event.currentTarget.value,
        });
    };

    const onAmountChange = (event: React.FormEvent<HTMLInputElement>) => {
        props.changeAmount(props.methodKey, event.currentTarget.value);
    };

    const onValidStateChange = (fieldName: string, errorMessages: string[]) => {
        props.onValidStateChange(fieldName, errorMessages, props.methodKey);
    };

    const paymentDataClasses = classNames({
        ["form__card-number"]: true,
    });

    const errors =
        props.methodKey in props.errors ? props.errors[props.methodKey] : {};

    const creditCardImage = creditCards;
    return (
        <div>
            <h4 className="checkout-step__bar-heading">{props.header}</h4>
            <div className="checkout-step__form-container checkout-step__form-container--cc checkout-step__form-container--blue">
                <div className="checkout-step__form-header">
                    <div className="checkout-step__form-header--cc-copy">
                        <h5 className="checkout-step__copy--bold">
                            {t`Secure Credit Card Payment`}
                        </h5>
                        <div>
                            {t`Place your cursor in the field below and type customer credit card info into the keypad.`}
                        </div>
                    </div>
                    <div className="checkout-step__form-header--cc-image">
                        <Image
                            src={creditCardImage}
                            className="checkout-step__form-image--cc responsive-img"
                            alt="Accepted Credit Cards: VISA, MasterCard, American Express or Discover"
                        />
                    </div>
                </div>
                <fieldset disabled={props.disabled} style={{ border: 0 }}>
                    <div className="checkout-step__copy--bold">
                        Encrypted Payment Data
                    </div>
                    <FormTextbox
                        inputRef={textBoxRef}
                        rows={3}
                        cols={40}
                        name="payment_data"
                        id="payment_data"
                        validation={["required"]}
                        showErrorMessages={props.showErrorMessages}
                        onValidStateChange={onValidStateChange}
                        autoComplete="payment-data"
                        className={paymentDataClasses}
                        uncontrolled={true}
                        value={methodData.payment_data}
                        errors={errors.payment_data}
                        onChange={onChange}
                        maxLength={BLUEFIN_PAYMENT_DATA_MAX_LENGTH}
                        warningTitle={
                            frameWarning
                                ? t`Possible incomplete card read`
                                : undefined
                        }
                        warningText={frameWarning ?? undefined}
                    />
                    {props.isSplitPay && (
                        <div className="checkout-step__payment-amount">
                            <div className="checkout-step__copy--bold">
                                Amount
                            </div>
                            <FormNumber
                                name="amount"
                                id="amount"
                                step={0.01}
                                autoComplete=""
                                validation={["required", "amount", "nonzero"]}
                                showErrorMessages={props.showErrorMessages}
                                onValidStateChange={onValidStateChange}
                                value={methodData.amount || undefined}
                                errors={errors.amount}
                                onChange={onAmountChange}
                                disabled={props.shouldFreezeAmount}
                            />
                        </div>
                    )}
                    {props.disabled && (
                        <div>
                            <h5>{t`Pre-Authorized`}</h5>
                            <p>
                                {interpolate(
                                    t`This card has been pre-authorized for the amount of %s`,
                                    [format.money(methodData.amount || "0.00")],
                                )}
                                .
                            </p>
                        </div>
                    )}
                </fieldset>
            </div>
        </div>
    );
};
